import { Box, HStack, Text, VStack } from "@chakra-ui/react"
import { useEffect, useMemo, useRef, useState } from "react"
import { useMapboxLoader } from "./useMapboxLoader"

type ResortMapTabProps = {
  convexHull?: string
  geometry?: string
}

const parseGeoJSON = (value?: string) => {
  if (!value) return null
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export default function ResortMapTab({ convexHull, geometry }: ResortMapTabProps) {
  const [geometryChoice, setGeometryChoice] = useState<"convexHull" | "geometry">
    (convexHull ? "convexHull" : "geometry")
  const [mapError, setMapError] = useState<string | null>(null)
  const mapRef = useRef<HTMLDivElement | null>(null)
  const mapInstanceRef = useRef<any>(null)
  const { ready: mapboxReady, error: mapboxLoadError } = useMapboxLoader()

  const selectedGeometryString = geometryChoice === "geometry" ? geometry : convexHull

  const availableGeometryOptions = useMemo(() => {
    const options: Array<"convexHull" | "geometry"> = []
    if (convexHull) options.push("convexHull")
    if (geometry) options.push("geometry")
    return options
  }, [convexHull, geometry])

  useEffect(() => {
    if (geometryChoice === "convexHull" && !convexHull && geometry) {
      setGeometryChoice("geometry")
    }
    if (geometryChoice === "geometry" && !geometry && convexHull) {
      setGeometryChoice("convexHull")
    }
  }, [geometryChoice, convexHull, geometry])

  const featureGeoJson = useMemo(() => {
    const parsed = parseGeoJSON(selectedGeometryString)
    if (!parsed) return null
    if (parsed.type === "FeatureCollection") return parsed
    if (parsed.type === "Feature") return parsed
    return { type: "Feature", geometry: parsed }
  }, [selectedGeometryString])

  useEffect(() => {
    if (!featureGeoJson || !mapRef.current || !mapboxReady) return

    const token = import.meta.env.VITE_MAPBOX_PUBLIC_TOKEN
    if (!token) {
      setMapError("Mapbox token not configured. Set VITE_MAPBOX_PUBLIC_TOKEN.")
      return
    }
    setMapError(null)

    try {
      const mapbox = window.mapboxgl
      mapbox.accessToken = token
      const geoJsonCollection =
        featureGeoJson.type === "FeatureCollection"
          ? featureGeoJson
          : { type: "FeatureCollection", features: [featureGeoJson] }

      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }

      const map = new mapbox.Map({
        container: mapRef.current,
        style: "mapbox://styles/mapbox/outdoors-v12",
        center: [0, 0],
        zoom: 2,
      })
      mapInstanceRef.current = map

      map.on("load", () => {
        map.addSource("resort-map", {
          type: "geojson",
          data: geoJsonCollection,
        })
        map.addLayer({
          id: "resort-map-fill",
          type: "fill",
          source: "resort-map",
          paint: {
            "fill-color": "#38a169",
            "fill-opacity": 0.25,
          },
          filter: ["any", ["==", ["geometry-type"], "Polygon"], ["==", ["geometry-type"], "MultiPolygon"]],
        })
        map.addLayer({
          id: "resort-map-line",
          type: "line",
          source: "resort-map",
          paint: {
            "line-color": "#38a169",
            "line-width": 2,
          },
        })
        map.addLayer({
          id: "resort-map-point",
          type: "circle",
          source: "resort-map",
          paint: {
            "circle-radius": 6,
            "circle-color": "#38a169",
          },
          filter: ["==", ["geometry-type"], "Point"],
        })

        const bounds = new mapbox.LngLatBounds()
        const addCoords = (coords: any) => {
          if (!coords) return
          if (typeof coords[0] === "number" && typeof coords[1] === "number") {
            bounds.extend(coords as [number, number])
            return
          }
          coords.forEach((c: any) => addCoords(c))
        }
        geoJsonCollection.features.forEach((feature: any) => addCoords(feature.geometry?.coordinates))

        if (bounds.isEmpty()) {
          const first = geoJsonCollection.features[0]?.geometry?.coordinates
          if (Array.isArray(first) && typeof first[0] === "number" && typeof first[1] === "number") {
            map.setCenter([first[0], first[1]])
            map.setZoom(12)
          }
        } else {
          map.fitBounds(bounds, { padding: 24, duration: 0 })
        }
      })
    } catch (err) {
      console.error(err)
      setMapError("Failed to render map")
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [featureGeoJson, mapboxReady])

  return (
    <VStack align="stretch" gap="3">
      <HStack justify="space-between" align="center">
        <Text fontWeight="semibold">Map preview</Text>
        <Box maxW="220px">
          <select
            value={geometryChoice}
            onChange={(e) => setGeometryChoice(e.target.value as "convexHull" | "geometry")}
            disabled={availableGeometryOptions.length <= 1}
            style={{
              width: "100%",
              backgroundColor: "rgb(55, 65, 81)",
              border: "1px solid rgb(75, 85, 99)",
              borderRadius: "0.375rem",
              padding: "0.5rem 0.75rem",
              fontSize: "0.875rem",
              color: "white"
            }}
          >
            <option value="convexHull">Convex hull</option>
            <option value="geometry">Geometry</option>
          </select>
        </Box>
      </HStack>
      {featureGeoJson ? (
        <Box
          ref={mapRef}
          borderRadius="lg"
          overflow="hidden"
          borderWidth="1px"
          borderColor="gray.700"
          bg="gray.800"
          minH="420px"
        />
      ) : (
        <Text color="gray.400" fontSize="sm">
          No geometry available for this resort.
        </Text>
      )}
      {mapError && (
        <Text color="red.300" fontSize="sm">
          {mapError}
        </Text>
      )}
      {mapboxLoadError && (
        <Text color="red.300" fontSize="sm">
          {mapboxLoadError}
        </Text>
      )}
    </VStack>
  )
}
